import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  LogOut, FileText, MessageSquare, Calendar, CreditCard,
  ClipboardList, Upload, Send, Loader2, User, Clock,
  CheckCircle, AlertCircle, Home
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = { new: "bg-blue-100 text-blue-700", contacted: "bg-yellow-100 text-yellow-700", "in-progress": "bg-purple-100 text-purple-700", completed: "bg-green-100 text-green-700", cancelled: "bg-red-100 text-red-700", scheduled: "bg-blue-100 text-blue-700", confirmed: "bg-green-100 text-green-700", pending: "bg-yellow-100 text-yellow-700", paid: "bg-green-100 text-green-700" };

export default function PatientDashboard() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [records, setRecords] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msgText, setMsgText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [sendingMsg, setSendingMsg] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [dash, docs, msgs, appts, recs, pays] = await Promise.all([
        axios.get(`${API}/patient/dashboard`, { withCredentials: true }),
        axios.get(`${API}/patient/documents`, { withCredentials: true }),
        axios.get(`${API}/patient/messages`, { withCredentials: true }),
        axios.get(`${API}/patient/appointments`, { withCredentials: true }),
        axios.get(`${API}/patient/records`, { withCredentials: true }),
        axios.get(`${API}/patient/payments`, { withCredentials: true }),
      ]);
      setData(dash.data);
      setDocuments(docs.data);
      setMessages(msgs.data);
      setAppointments(appts.data);
      setRecords(recs.data);
      setPayments(pays.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) navigate("/patient/login");
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "patient") { navigate("/patient/login"); return; }
    fetchAll();
  }, [user, authLoading, navigate, fetchAll]);

  const uploadDoc = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("doc_type", "medical_report");
      await axios.post(`${API}/patient/documents`, fd, { withCredentials: true });
      toast.success("Document uploaded"); fetchAll();
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const sendMessage = async () => {
    if (!msgText.trim()) return;
    setSendingMsg(true);
    try {
      await axios.post(`${API}/patient/messages`, { content: msgText }, { withCredentials: true });
      setMsgText(""); fetchAll();
    } catch { toast.error("Failed to send"); } finally { setSendingMsg(false); }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };

  if (authLoading || loading) {
    return <div className="min-h-[80vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#00A4A9]" /></div>;
  }

  return (
    <div data-testid="patient-dashboard" className="min-h-screen bg-[#F9F9F8]">
      <div className="bg-[#00A4A9] text-white px-4 sm:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-outfit text-lg font-medium" data-testid="patient-portal-title">Patient Portal</h1>
            <p className="text-sm text-white/70">Welcome, {data?.profile?.name}</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/"><Button variant="ghost" size="sm" className="text-white hover:bg-white/10"><Home className="w-4 h-4 mr-1.5" /> Home</Button></Link>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/10" data-testid="patient-logout-btn"><LogOut className="w-4 h-4 mr-1.5" /> Logout</Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="patient-stats">
          {[
            { l: "My Inquiries", v: data?.inquiries?.length || 0, icon: ClipboardList, c: "text-[#00A4A9]" },
            { l: "Documents", v: data?.documents_count || 0, icon: FileText, c: "text-green-600" },
            { l: "Unread Messages", v: data?.unread_messages || 0, icon: MessageSquare, c: "text-amber-600" },
            { l: "Appointments", v: appointments.length, icon: Calendar, c: "text-purple-600" },
          ].map((s) => (
            <div key={s.l} className="bg-white rounded-xl p-5 border border-slate-200">
              <div className="flex items-center justify-between mb-2"><span className="text-xs text-slate-500">{s.l}</span><s.icon className={`w-4 h-4 ${s.c}`} /></div>
              <div className="font-outfit text-2xl font-semibold text-[#03045e]">{s.v}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="overview">
          <TabsList className="bg-white border border-slate-200 rounded-xl p-1 mb-6 flex flex-wrap" data-testid="patient-tabs">
            <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-[#00A4A9] data-[state=active]:text-white"><ClipboardList className="w-4 h-4 mr-1.5" /> Overview</TabsTrigger>
            <TabsTrigger value="documents" className="rounded-lg data-[state=active]:bg-[#00A4A9] data-[state=active]:text-white"><FileText className="w-4 h-4 mr-1.5" /> Documents</TabsTrigger>
            <TabsTrigger value="messages" className="rounded-lg data-[state=active]:bg-[#00A4A9] data-[state=active]:text-white"><MessageSquare className="w-4 h-4 mr-1.5" /> Messages</TabsTrigger>
            <TabsTrigger value="appointments" className="rounded-lg data-[state=active]:bg-[#00A4A9] data-[state=active]:text-white"><Calendar className="w-4 h-4 mr-1.5" /> Appointments</TabsTrigger>
            <TabsTrigger value="records" className="rounded-lg data-[state=active]:bg-[#00A4A9] data-[state=active]:text-white"><ClipboardList className="w-4 h-4 mr-1.5" /> Records</TabsTrigger>
            <TabsTrigger value="payments" className="rounded-lg data-[state=active]:bg-[#00A4A9] data-[state=active]:text-white"><CreditCard className="w-4 h-4 mr-1.5" /> Payments</TabsTrigger>
          </TabsList>

          {/* Overview - Inquiries & Timeline */}
          <TabsContent value="overview">
            <h3 className="font-outfit text-lg font-medium text-[#03045e] mb-4">Treatment Timeline</h3>
            {(!data?.inquiries || data.inquiries.length === 0) ? (
              <div className="bg-white rounded-xl border p-12 text-center"><ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No inquiries yet.</p><Link to="/contact"><Button className="mt-4 rounded-full bg-[#00A4A9] text-white">Submit Your First Inquiry</Button></Link></div>
            ) : (
              <div className="space-y-4" data-testid="inquiry-timeline">{data.inquiries.map((inq, i) => (
                <div key={inq.id || i} className="bg-white rounded-xl border p-5">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-outfit font-medium text-[#03045e]">{inq.treatment}</h4>
                    <Badge className={statusColors[inq.status] || "bg-slate-100"}>{inq.status}</Badge>
                  </div>
                  <p className="text-sm text-slate-500">{inq.message || "No additional details"}</p>
                  <div className="text-xs text-slate-400 mt-2"><Clock className="w-3 h-3 inline mr-1" />{new Date(inq.created_at).toLocaleDateString()}</div>
                </div>
              ))}</div>
            )}
          </TabsContent>

          {/* Documents */}
          <TabsContent value="documents">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-outfit text-lg font-medium text-[#03045e]">My Documents</h3>
              <label className="cursor-pointer">
                <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={uploadDoc} data-testid="doc-upload-input" />
                <Button className="rounded-full bg-[#00A4A9] text-white" disabled={uploading} asChild><span><Upload className="w-4 h-4 mr-1.5" />{uploading ? "Uploading..." : "Upload Document"}</span></Button>
              </label>
            </div>
            {documents.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center"><FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No documents uploaded yet.</p></div>
            ) : (
              <div className="space-y-3" data-testid="documents-list">{documents.map((doc) => (
                <div key={doc.id} className="bg-white rounded-xl border p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3"><FileText className="w-8 h-8 text-[#00A4A9]" /><div><div className="text-sm font-medium text-[#03045e]">{doc.filename}</div><div className="text-xs text-slate-400">{doc.type} &middot; {new Date(doc.uploaded_at).toLocaleDateString()}</div></div></div>
                  <a href={doc.url} target="_blank" rel="noopener noreferrer"><Button variant="outline" size="sm" className="rounded-full">View</Button></a>
                </div>
              ))}</div>
            )}
          </TabsContent>

          {/* Messages */}
          <TabsContent value="messages">
            <h3 className="font-outfit text-lg font-medium text-[#03045e] mb-4">Messages with Coordinator</h3>
            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="h-80 overflow-y-auto p-4 space-y-3" data-testid="messages-list">
                {messages.length === 0 ? (
                  <p className="text-center text-slate-400 mt-20">No messages yet. Start a conversation!</p>
                ) : messages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.sender_role === "patient" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${msg.sender_role === "patient" ? "bg-[#00A4A9] text-white" : "bg-slate-100 text-slate-700"}`}>
                      <p className="text-sm">{msg.content}</p>
                      <p className={`text-[10px] mt-1 ${msg.sender_role === "patient" ? "text-white/60" : "text-slate-400"}`}>{new Date(msg.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t p-3 flex gap-2">
                <Input value={msgText} onChange={(e) => setMsgText(e.target.value)} placeholder="Type a message..." className="flex-1" onKeyDown={(e) => e.key === "Enter" && sendMessage()} data-testid="message-input" />
                <Button onClick={sendMessage} disabled={sendingMsg || !msgText.trim()} className="bg-[#00A4A9] text-white rounded-full" data-testid="send-message-btn"><Send className="w-4 h-4" /></Button>
              </div>
            </div>
          </TabsContent>

          {/* Appointments */}
          <TabsContent value="appointments">
            <h3 className="font-outfit text-lg font-medium text-[#03045e] mb-4">My Appointments</h3>
            {appointments.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center"><Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No appointments scheduled yet.</p><p className="text-xs text-slate-400 mt-1">Your coordinator will schedule appointments for you.</p></div>
            ) : (
              <div className="space-y-3" data-testid="appointments-list">{appointments.map((a) => (
                <div key={a.id} className="bg-white rounded-xl border p-5">
                  <div className="flex items-center justify-between mb-2"><h4 className="font-outfit font-medium text-[#03045e]">{a.type}</h4><Badge className={statusColors[a.status] || "bg-slate-100"}>{a.status}</Badge></div>
                  <div className="grid grid-cols-2 gap-2 text-sm text-slate-600"><span>Doctor: {a.doctor}</span><span>Hospital: {a.hospital}</span><span>Date: {a.date}</span><span>Time: {a.time}</span></div>
                  {a.notes && <p className="text-sm text-slate-500 mt-2 bg-slate-50 rounded-lg p-3">{a.notes}</p>}
                </div>
              ))}</div>
            )}
          </TabsContent>

          {/* Records */}
          <TabsContent value="records">
            <h3 className="font-outfit text-lg font-medium text-[#03045e] mb-4">Medical Records</h3>
            {records.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center"><ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No medical records available yet.</p></div>
            ) : (
              <div className="space-y-3" data-testid="records-list">{records.map((r) => (
                <div key={r.id} className="bg-white rounded-xl border p-5">
                  <h4 className="font-outfit font-medium text-[#03045e] mb-1">{r.title}</h4>
                  <p className="text-sm text-slate-600 mb-2">{r.description}</p>
                  <div className="text-xs text-slate-400">Dr. {r.doctor} &middot; {r.hospital} &middot; {new Date(r.date).toLocaleDateString()}</div>
                </div>
              ))}</div>
            )}
          </TabsContent>

          {/* Payments */}
          <TabsContent value="payments">
            <h3 className="font-outfit text-lg font-medium text-[#03045e] mb-4">Payment History</h3>
            {payments.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center"><CreditCard className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No payment records yet.</p></div>
            ) : (
              <div className="space-y-3" data-testid="payments-list">{payments.map((p) => (
                <div key={p.id} className="bg-white rounded-xl border p-5 flex items-center justify-between">
                  <div><h4 className="font-outfit font-medium text-[#03045e]">{p.description}</h4><div className="text-xs text-slate-400">{new Date(p.date).toLocaleDateString()} {p.method && `&middot; ${p.method}`}</div></div>
                  <div className="text-right"><div className="font-outfit text-lg font-semibold text-[#03045e]">{p.currency} {p.amount}</div><Badge className={statusColors[p.status] || "bg-slate-100"}>{p.status}</Badge></div>
                </div>
              ))}</div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
